import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenant } from "../tenant";
import { ThemeControls } from "../ThemeControls";
import { BrandMark } from "../BrandMark";

interface Section {
  h: string;
  p: string[];
}

function LegalShell({ title, updated, children }: { title: string; updated: string; children: ReactNode }) {
  const tenant = useTenant();
  const { t } = useTranslation();
  return (
    <div className="legal">
      <header className="legal__top">
        <Link to="/login" className="legal__brand">
          <BrandMark icon={tenant.theme.icon} />
          <span className="brand__name">{tenant.name}</span>
        </Link>
        <div className="legal__top-right">
          <ThemeControls />
          <Link to="/login" className="auth__link">
            {t("forgot.backToLogin")}
          </Link>
        </div>
      </header>
      <article className="legal__doc">
        <h1>{title}</h1>
        <p className="legal__updated">{updated}</p>
        {children}
      </article>
    </div>
  );
}

function Sections({ items }: { items: Section[] }) {
  return (
    <>
      {items.map((s) => (
        <section key={s.h} className="legal__section">
          <h2>{s.h}</h2>
          {s.p.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </section>
      ))}
    </>
  );
}

export function PrivacyPage() {
  const tenant = useTenant();
  const co = tenant.name;
  const sections: Section[] = [
    {
      h: "1. Overview",
      p: [
        `This Privacy Policy explains how ${co} ("we", "us") collects, uses, and protects your information when you use this workspace. We are committed to handling your data responsibly and only for the purposes described here.`,
      ],
    },
    {
      h: "2. Information we collect",
      p: [
        "Account information you provide — such as your name, email address, role, and profile image.",
        "Content you create within the workspace, including projects, records, and files.",
        "Technical data such as sign-in events and usage needed to operate and secure the service.",
      ],
    },
    {
      h: "3. How we use information",
      p: [
        "To provide and maintain your workspace, authenticate you, and deliver the features enabled for your organization.",
        "To keep the service secure, prevent abuse, and meet legal obligations.",
        "We do not sell your personal information.",
      ],
    },
    {
      h: "4. Data isolation & storage",
      p: [
        "Each organization's business data is stored in its own isolated database. Access is restricted to authorized members of that organization based on their role.",
      ],
    },
    {
      h: "5. AI processing",
      p: [
        "When you use AI features, your prompts and the necessary context are processed by the configured AI provider solely to generate a response. Usage is metered per organization.",
      ],
    },
    {
      h: "6. Your rights",
      p: [
        "You may access and update your profile at any time, and request correction or deletion of your personal data by contacting your workspace administrator.",
      ],
    },
    {
      h: "7. Contact",
      p: [`For privacy questions, contact your ${co} administrator.`],
    },
  ];
  return (
    <LegalShell title="Privacy Policy" updated="Last updated: 2026">
      <Sections items={sections} />
    </LegalShell>
  );
}

export function TermsPage() {
  const tenant = useTenant();
  const co = tenant.name;
  const sections: Section[] = [
    {
      h: "1. Acceptance of terms",
      p: [
        `By accessing this workspace provided by ${co}, you agree to these Terms of Use. If you do not agree, do not use the service.`,
      ],
    },
    {
      h: "2. Accounts & access",
      p: [
        "You are responsible for maintaining the confidentiality of your credentials and for all activity under your account.",
        "Access is granted by your organization's administrator and scoped to your assigned role.",
      ],
    },
    {
      h: "3. Acceptable use",
      p: [
        "You agree not to misuse the service, attempt to access data belonging to other organizations, or interfere with its operation or security.",
      ],
    },
    {
      h: "4. Your content",
      p: [
        "You retain ownership of the content you create. You grant us the limited rights necessary to host and process it in order to provide the service.",
      ],
    },
    {
      h: "5. Availability",
      p: [
        "The service is provided on an “as is” basis. We work to keep it available and secure, but do not guarantee uninterrupted operation.",
      ],
    },
    {
      h: "6. Changes",
      p: [
        "We may update these terms from time to time. Continued use after changes take effect constitutes acceptance of the revised terms.",
      ],
    },
    {
      h: "7. Contact",
      p: [`Questions about these terms can be directed to your ${co} administrator.`],
    },
  ];
  return (
    <LegalShell title="Terms of Use" updated="Last updated: 2026">
      <Sections items={sections} />
    </LegalShell>
  );
}
